import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getAllCoursesUnified } from '@/utils/allCourses';

export interface AutoEnrollmentResult {
  success: boolean;
  assignedCount: number;
  errors: string[];
}

// Auto-assign all courses to staff/admin users
export const autoAssignAllCoursesToStaffAdmin = async (userId: string, userRole: string): Promise<AutoEnrollmentResult> => {
  if (!['admin', 'staff'].includes(userRole)) {
    return { success: false, assignedCount: 0, errors: ['User is not staff or admin'] };
  }

  try {
    console.log('Auto-assigning all courses to staff/admin user:', userId, 'role:', userRole);
    
    // Get all available courses
    const allCourses = getAllCoursesUnified();
    const courseIds = allCourses.map(course => course.id);
    
    console.log('Found', courseIds.length, 'courses to auto-assign');

    // Use enhanced bulk assignment function for staff/admin
    const { data, error } = await (supabase as any).rpc('admin_bulk_assign_courses_enhanced', {
      p_user_ids: [userId],
      p_course_ids: courseIds,
      p_assigned_by: userId // Self-assignment for staff/admin
    });

    if (error) {
      console.error('Error in auto-assignment:', error);
      return { 
        success: false, 
        assignedCount: 0, 
        errors: [error.message] 
      };
    }

    const response = data as any;
    
    if (response?.success) {
      const assignedCount = response.success_count || 0;
      console.log('Auto-assignment completed:', assignedCount, 'courses assigned');
      
      return {
        success: true,
        assignedCount,
        errors: response.errors || []
      };
    } else {
      return {
        success: false,
        assignedCount: 0,
        errors: [response?.error || 'Unknown error in auto-assignment']
      };
    }
  } catch (error) {
    console.error('Error in auto-assignment service:', error);
    return {
      success: false,
      assignedCount: 0,
      errors: [(error as any)?.message || 'Failed to auto-assign courses']
    };
  }
};

// Check if user needs auto-assignment (staff/admin with no assignments)
export const checkAndPerformAutoAssignment = async (userId: string, userRole: string): Promise<void> => {
  if (!['admin', 'staff'].includes(userRole)) {
    return; // Only auto-assign for staff/admin
  }

  try {
    // Check if user already has assignments
    const { data: existingAssignments, error } = await supabase
      .from('course_assignments')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .limit(5);

    if (error) {
      console.error('Error checking existing assignments:', error);
      return;
    }

    // If user has fewer than 5 assignments, perform auto-assignment
    if (!existingAssignments || existingAssignments.length < 5) {
      console.log('User has few/no assignments, performing auto-assignment');
      
      const result = await autoAssignAllCoursesToStaffAdmin(userId, userRole);
      
      if (result.success && result.assignedCount > 0) {
        toast.success(`Auto-assigned ${result.assignedCount} courses for ${userRole} access`);
      } else if (result.errors.length > 0) {
        console.warn('Auto-assignment had errors:', result.errors);
      }
    } else {
      console.log('User already has sufficient assignments, skipping auto-assignment');
    }
  } catch (error) {
    console.error('Error in auto-assignment check:', error);
  }
};